import React, { useEffect, useState } from 'react';
import StableMuiDialog from '../../common/StableMuiDialog.js';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined';
import HomeRepairServiceOutlinedIcon from '@mui/icons-material/HomeRepairServiceOutlined';
// import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import { SALE_ISSUE_OPTIONS } from '../../common/flagBadSaleHelper.js';

const colors = {
  bgPrimary: '#ffffff',
  bgSecondary: '#f1efe8',
  textPrimary: '#1a1a18',
  textSecondary: '#5f5e5a',
  textTertiary: '#888780',
  borderTertiary: 'rgba(0,0,0,0.12)',
  borderSecondary: 'rgba(0,0,0,0.22)',
  accent: '#185FA5',
  accentBg: '#E6F1FB',
};

const ISSUE_ICONS = {
  NotArmsLength: <FlagOutlinedIcon fontSize="inherit" />,
  InventoryMismatch: <HomeRepairServiceOutlinedIcon fontSize="inherit" />,
  // SaleConditionIssue: <ReportProblemOutlinedIcon fontSize="inherit" />,
};

const SectionLabel = ({ children }) => (
  <Typography
    component="p"
    sx={{
      fontSize: 11,
      fontWeight: 500,
      letterSpacing: '0.06em',
      textTransform: 'uppercase',
      color: colors.textTertiary,
      m: '0 0 8px',
    }}
  >
    {children}
  </Typography>
);

const IssueCard = ({ active, icon, name, description, onClick }) => (
  <Box
    onClick={onClick}
    role="button"
    tabIndex={0}
    onKeyDown={(e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onClick();
      }
    }}
    sx={{
      border: '1.5px solid',
      borderColor: active ? colors.accent : colors.borderTertiary,
      borderRadius: '8px',
      p: '12px 14px',
      cursor: 'pointer',
      height: '100%',
      transition: 'border-color 0.15s, background 0.15s',
      background: active ? colors.accentBg : colors.bgPrimary,
      '&:hover': { borderColor: active ? colors.accent : colors.borderSecondary },
    }}
  >
    <Box sx={{ fontSize: 20, color: active ? colors.accent : colors.textSecondary, mb: 0.75 }}>
      {icon}
    </Box>
    <Typography sx={{ fontSize: 13, fontWeight: 500, color: colors.textPrimary, m: '0 0 2px' }}>
      {name}
    </Typography>
    <Typography sx={{ fontSize: 11, color: colors.textSecondary, m: 0, lineHeight: 1.4 }}>
      {description}
    </Typography>
  </Box>
);

const formatSaleDate = (dateStr) => {
  if (!dateStr) return '';
  const match = String(dateStr).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) return `${match[2]}/${match[3]}/${match[1].slice(2)}`;
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: '2-digit' });
};

const FlagBadSaleDialog = ({
  open,
  onClose,
  onConfirm,
  submitting = false,
  subtitle,
  sales = [],
  confirmLabel = 'Flag sale',
}) => {
  const [saleIssue, setSaleIssue] = useState(null);
  const [memo, setMemo] = useState('');

  useEffect(() => {
    if (!open) return;
    setSaleIssue(null);
    setMemo('');
  }, [open]);

  const handleFlag = () => {
    if (!saleIssue || submitting) return;
    onConfirm({ SaleIssue: saleIssue, Memo: memo.trim() });
  };

  return (
    <StableMuiDialog
      open={open}
      onClose={submitting ? undefined : onClose}
      maxWidth={false}
      container={() => document.body}
      style={{ zIndex: 10001 }}
      PaperProps={{
        sx: {
          width: '100%',
          maxWidth: 460,
          borderRadius: '16px',
          border: `0.5px solid ${colors.borderSecondary}`,
          bgcolor: colors.bgPrimary,
          m: 2,
        },
      }}
    >
      <Box sx={{ p: 3 }}>
        <Typography
          component="h2"
          sx={{ fontSize: 17, fontWeight: 500, color: colors.textPrimary, m: '0 0 4px' }}
        >
          Flag bad sale
        </Typography>
        {subtitle && (
          <Typography sx={{ fontSize: 13, color: colors.textSecondary, m: sales.length ? '0 0 4px' : '0 0 20px' }}>
            {subtitle}
          </Typography>
        )}
        {sales.length > 0 && (
          <Box sx={{ mb: 2.5, maxHeight: 140, overflow: 'auto' }}>
            {sales.map((sale, index) => {
              const price = Number(sale.price);
              const priceText = sale.price != null && sale.price !== '' && !Number.isNaN(price)
                ? `$${price.toLocaleString()}`
                : 'No sale price';
              const dateText = formatSaleDate(sale.date);
              return (
                <Typography key={`${sale.address || 'sale'}-${index}`} sx={{ fontSize: 13, color: colors.textPrimary, lineHeight: 1.45 }}>
                  {sale.address ? `${sale.address} — ` : ''}
                  {priceText}{dateText ? ` on ${dateText}` : ''}
                </Typography>
              );
            })}
          </Box>
        )}

        <SectionLabel>Issue</SectionLabel>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 1,
            mb: 2.5,
            alignItems: 'stretch',
          }}
        >
          {SALE_ISSUE_OPTIONS.map((option) => (
            <Box key={option.value} sx={{ height: '100%' }}>
              <IssueCard
                active={saleIssue === option.value}
                icon={ISSUE_ICONS[option.value]}
                name={option.name}
                description={option.description}
                onClick={() => setSaleIssue(option.value)}
              />
            </Box>
          ))}
        </Box>

        <SectionLabel>Memo</SectionLabel>
        <TextField
          value={memo}
          onChange={(e) => setMemo(e.target.value)}
          placeholder="Optional note"
          multiline
          minRows={3}
          fullWidth
          disabled={submitting}
          sx={{
            mb: 2.5,
            '& .MuiOutlinedInput-root': {
              fontSize: 13,
              borderRadius: '8px',
              '& fieldset': { borderColor: colors.borderTertiary },
            },
          }}
        />

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1, pt: 0.5 }}>
          <Button
            onClick={onClose}
            disabled={submitting}
            sx={{
              fontSize: 13,
              fontWeight: 500,
              color: colors.textSecondary,
              border: `0.5px solid ${colors.borderSecondary}`,
              borderRadius: '8px',
              px: 2,
              py: 1,
              textTransform: 'none',
              minWidth: 'auto',
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleFlag}
            variant="contained"
            disableElevation
            disabled={!saleIssue || submitting}
            sx={{
              fontSize: 13,
              fontWeight: 500,
              bgcolor: colors.accent,
              borderRadius: '8px',
              px: 2.5,
              py: 1,
              textTransform: 'none',
              minWidth: 'auto',
              '&:hover': { bgcolor: '#144f8a' },
            }}
          >
            {submitting ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : confirmLabel}
          </Button>
        </Box>
      </Box>
    </StableMuiDialog>
  );
};

export default FlagBadSaleDialog;
